import { useMutation, useQueryClient } from '@tanstack/react-query'

import { graphql_call as graphql_admin_call } from '../../schemas/server_admin_query.ts'

import { mutation_approve_fund_nav } from './fund_graphql.ts'
import { keys } from './keystore.ts'

export interface ApproveFundNavInput {
  fundId: string
  effectiveAt: string
  overwrite?: boolean
}

export interface ApproveFeederNavInput {
  feederId: string
  fundId: string
  effectiveAt: string
}

export interface ApproveFundNavResponse {
  fund_nav: {
    published_at: string | null
    effective_at: string
    cash_total_value: number
    assets_total_value: number
    valuation: number
    total_shares: number
    price_per_share: number
  } | null
  errors?: string[]
}

export function useApproveFundNav() {
  const queryClient = useQueryClient()

  return useMutation<ApproveFundNavResponse, Error, ApproveFundNavInput>({
    // eslint-disable-next-line complexity
    mutationFn: async (
      input: ApproveFundNavInput,
    ): Promise<ApproveFundNavResponse> => {
      if (!input.fundId) {
        throw new Error('Fund ID is required')
      }

      if (!input.effectiveAt) {
        throw new Error('Effective date/time is required')
      }

      const response = await graphql_admin_call({
        query: mutation_approve_fund_nav,
        variables: {
          fund_id: input.fundId,
          effective_at: input.effectiveAt,
          overwrite: input.overwrite ?? false,
        },
      })

      if (!response) throw new Error('Failed to approve fund NAV')
      const result = response as ApproveFundNavResponse

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors.join(', '))
      }

      return result
    },
    onSuccess: (data, variables) => {
      if (data.errors?.length === 0) {
        void queryClient.invalidateQueries({
          queryKey: keys.admin_funds.detail(variables.fundId).queryKey,
        })
        void queryClient.invalidateQueries({
          queryKey: keys.admin_funds.all.queryKey,
        })
      }
    },
  })
}
